/**
 * Phase 6: Alerts Center Component
 *
 * Notification bell with dropdown showing user alerts.
 * Shows unread count badge and allows dismissing/reading alerts.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { IoNotifications, IoClose, IoCheckmarkDone, IoTrash } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'
import {
  getAlerts,
  getUnreadAlertCount,
  markAlertsRead,
  dismissAlert,
  dismissAllAlerts,
  waitForAuthReady,
  type UserAlert,
} from '../api'

// Alert type display configuration
const ALERT_TYPE_CONFIG: Record<string, { icon: string; color: string }> = {
  lineup_confirmed: { icon: 'XI', color: 'var(--accent)' },
  builder_invalid: { icon: '!', color: 'var(--red)' },
  booking_warning: { icon: 'Y', color: 'var(--yellow)' },
  goal: { icon: 'G', color: 'var(--green)' },
  card: { icon: 'C', color: 'var(--yellow)' },
  player_milestone: { icon: 'M', color: 'var(--accent)' },
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface AlertsDropdownProps {
  alerts: UserAlert[]
  onClose: () => void
  onDismiss: (alertId: number) => void
  onDismissAll: () => void
  onMarkAllRead: () => void
  onAlertClick: (alert: UserAlert) => void
  loading: boolean
}

function AlertsDropdown({
  alerts,
  onClose,
  onDismiss,
  onDismissAll,
  onMarkAllRead,
  onAlertClick,
  loading,
}: AlertsDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const unreadAlerts = alerts.filter((a) => !a.is_read)

  return (
    <div className="alerts-dropdown" ref={dropdownRef}>
      <div className="alerts-dropdown-header">
        <span className="alerts-dropdown-title">Notifications</span>
        {alerts.length > 0 && (
          <div className="alerts-dropdown-actions">
            {unreadAlerts.length > 0 && (
              <button
                className="alerts-action-btn"
                onClick={onMarkAllRead}
                title="Mark all as read"
              >
                <IoCheckmarkDone size={16} />
              </button>
            )}
            <button
              className="alerts-action-btn"
              onClick={onDismissAll}
              title="Dismiss all"
            >
              <IoTrash size={16} />
            </button>
          </div>
        )}
      </div>

      <div className="alerts-dropdown-body">
        {loading ? (
          <div className="alerts-loading">Loading...</div>
        ) : alerts.length === 0 ? (
          <div className="alerts-empty">No notifications</div>
        ) : (
          alerts.map((alert) => {
            const config = ALERT_TYPE_CONFIG[alert.alert_type] || {
              icon: '?',
              color: 'var(--muted)',
            }

            return (
              <div
                key={alert.id}
                className={`alert-item ${!alert.is_read ? 'unread' : ''}`}
                onClick={() => onAlertClick(alert)}
              >
                <div
                  className="alert-icon"
                  style={{ backgroundColor: config.color }}
                >
                  {config.icon}
                </div>
                <div className="alert-content">
                  <div className="alert-title">{alert.title}</div>
                  {alert.message && (
                    <div className="alert-message">{alert.message}</div>
                  )}
                  <div className="alert-time">{formatTimeAgo(alert.created_at)}</div>
                </div>
                <button
                  className="alert-dismiss"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismiss(alert.id)
                  }}
                  title="Dismiss"
                >
                  <IoClose size={16} />
                </button>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export function AlertsCenter() {
  const navigate = useNavigate()
  const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false }
  const [isOpen, setIsOpen] = useState(false)
  const [alerts, setAlerts] = useState<UserAlert[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)

  // Fetch unread count periodically
  useEffect(() => {
    if (!auth.isSignedIn) return

    const fetchUnreadCount = async () => {
      try {
        await waitForAuthReady()
        const { unread_count } = await getUnreadAlertCount()
        setUnreadCount(unread_count)
      } catch (err) {
        if (err instanceof Error && /401|403/.test(err.message)) {
          return
        }
        console.error('Failed to fetch unread count:', err)
      }
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000) // Every 30s
    return () => clearInterval(interval)
  }, [auth.isSignedIn])

  // Fetch alerts when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      getAlerts({ limit: 20 })
        .then(({ alerts, unread_count }) => {
          setAlerts(alerts)
          setUnreadCount(unread_count)
        })
        .catch((err) => console.error('Failed to fetch alerts:', err))
        .finally(() => setLoading(false))
    }
  }, [isOpen])

  const handleDismiss = useCallback(async (alertId: number) => {
    try {
      await dismissAlert(alertId)
      setAlerts((prev) => prev.filter((a) => a.id !== alertId))
      setUnreadCount((prev) => {
        const wasDismissedUnread = alerts.find((a) => a.id === alertId && !a.is_read)
        return wasDismissedUnread ? prev - 1 : prev
      })
    } catch (err) {
      console.error('Failed to dismiss alert:', err)
    }
  }, [alerts])

  const handleDismissAll = useCallback(async () => {
    try {
      await dismissAllAlerts()
      setAlerts([])
      setUnreadCount(0)
    } catch (err) {
      console.error('Failed to dismiss all alerts:', err)
    }
  }, [])

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAlertsRead()
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })))
      setUnreadCount(0)
    } catch (err) {
      console.error('Failed to mark alerts as read:', err)
    }
  }, [])

  const handleAlertClick = useCallback(
    async (alert: UserAlert) => {
      // Mark as read
      if (!alert.is_read) {
        try {
          await markAlertsRead([alert.id])
          setAlerts((prev) =>
            prev.map((a) => (a.id === alert.id ? { ...a, is_read: true } : a))
          )
          setUnreadCount((prev) => Math.max(0, prev - 1))
        } catch (err) {
          console.error('Failed to mark alert as read:', err)
        }
      }

      // Navigate to relevant page
      if (alert.fixture_id) {
        navigate(`/fixture/${alert.fixture_id}`)
        setIsOpen(false)
      }
    },
    [navigate]
  )

  return (
    <div className="alerts-center">
      <button
        className={`alerts-bell ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <IoNotifications size={20} />
        {unreadCount > 0 && (
          <span className="alerts-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <AlertsDropdown
          alerts={alerts}
          onClose={() => setIsOpen(false)}
          onDismiss={handleDismiss}
          onDismissAll={handleDismissAll}
          onMarkAllRead={handleMarkAllRead}
          onAlertClick={handleAlertClick}
          loading={loading}
        />
      )}
    </div>
  )
}
